import React from 'react';
import Section from './project-section';
import Valorant from './valorant';
import ENGS21 from './engs21';
import RLRobot from './rl-robot';
import Squirrel from '../media/squirrel.png';
import Moonboard from '../media/moonboard.jpg';
import Vox from '../media/vox.png';
import SLAR from '../media/slar.jpg';
import MANI from '../media/MANI.png';
import RL from '../media/RL_robot.gif';
import wiring from '../media/engs/wiring.png';
import comp from '../media/valorant/win_rate_comp.png';
import shimmer from '../media/shimmer/shimmer.webp';
import jBluff from '../media/j-bluff/live-round-probe-and-pin.gif';
import walking from '../media/walking-simulation/trip-playback-map-only.gif';
import bingo from '../media/library-bingo/board-filling-as-books-are-added.gif';
import knitting from '../media/knitting/pixel-option-invader-demo.gif';
import setGame from '../media/set-game/pipeline-steps.webp';
import market from '../media/kobold-broker/game-marketplace-buy.gif';
import koboldDemo from '../media/kobold-broker/protocol-parser-and-pricing-demo.gif';
import jlensRank from '../media/j-lens-injection/fig2-seven-words-rank.png';
import jlensAuc from '../media/j-lens-injection/fig3-auc.png';
import jlensSplit from '../media/j-lens-injection/fig4-resist-comply.png';
import jlensPairs from '../media/j-lens-injection/fig5-within-prompt-auc.png';
import jlensPdf from '../media/j-lens-injection/j-lens-prompt-injection.pdf';

const jlensDetectionFigures = [
  { src: jlensRank, alt: 'J-lens rank of the seven words at each block', caption: 'Injections terms rank highly in the J-lens band, highest for fraud text.' },
  {
    src: jlensAuc,
    alt: 'AUC of the readout against each comparison set',
    caption: 'The J-lens separated the injections from the same banking task with no injection (AUC 0.984; Figures 2 and 3). '
      + 'But against fraud text containing list words or their synonyms, the fraud text scored higher than the attacks (AUC 0.02 and 0.25). '
      + 'Against fraud text with neither, the scores were indistinguishable (AUC 0.44).',
  },
];

const jlensCausationFigures = [
  { src: jlensSplit, alt: 'resist and comply counts for each task', caption: 'Split resist/comply behavior for different tasks.' },
  {
    src: jlensPairs,
    alt: 'within-prompt AUC for complied vs resisted runs',
    caption: 'Within-prompt AUC for complied vs resisted over those 112 pairs. Shows each readout statistic and the logit-lens baseline. '
      + 'All three sit near 0.5 AUC. None of the metrics separated the complied from the resisted runs better than chance.',
  },
];

const koboldFigures = [
  { src: market, alt: 'buying an item on the Dark and Darker marketplace', caption: 'Video: Dimo - Dark and Darker' },
  { src: koboldDemo, alt: 'terminal demo of packet reassembly, protobuf decoding, and pricing' },
];

// Every project page, in display order. `short` labels the grid tiles; `body`
// is the writeup rendered under the shared page header. `link` and `pdf`,
// when set, are shown above the writeup.
const projects = [
  {
    slug: 'j-lens-injection',
    title: 'Does the J-lens work as a Prompt Injection Detector?',
    short: 'J-lens Injection Detection',
    date: 'October 2026',
    pdf: jlensPdf,
    img: jlensRank,
    body: (
      <>
        <Section>
          <p>
            In Anthropic&apos;s global workspace paper (
            <a href="https://transformer-circuits.pub/2026/workspace/index.html">Gurnee et al. 2026</a>
            , §5.2), they included a case study showing silent recognition of a prompt injection.
            They asserted that &quot;the model had (correctly) identified the results as a prompt-injection attempt, which led it to omit mention of the results entirely&quot;.
            This makes two claims:
          </p>
          <ol>
            <li>Detection: That the J-lens readout of those 7 tokens (fake, injection, false, prompt, fraud, poison, 假) marks this text as an attack.</li>
            <li>Causation: That the detection of the prompt injection is what led to the omission, the refusal to act on those results.</li>
          </ol>
          <p>
            The J-lens doesn&apos;t require training, just a single fitting process.
            If it were a prompt injection detector causally linked to compliance, it would be a training-free way to detect and refuse prompt injections.
          </p>
        </Section>
        <Section title="High-level takeaways">
          <ul>
            <li><b>It did flag prompt injections</b>: using those 7 tokens, the J-lens readout separated prompt injections from clean generations (AUC 0.984).</li>
            <li><b>But it also flagged non-injection content about fraud</b>: this method ranked benign fraud texts even higher than injections (AUC 0.02 for texts containing words from the list)</li>
            <li><b>Detection strength didn&apos;t track compliance</b>: the strength of detection showed no signal for or against causation (AUC 0.51).</li>
          </ul>
        </Section>
        <Section title="Experiment 1 — Injection vs Clean vs Fraud Text" figures={jlensDetectionFigures}>
          <p>
            I ran Qwen 3.6-27B against the AgentDojo prompt injection environment and measured the J-lens readout of camilablank/workspace-lenses/qwen3.6-27b/j-lens.
            I ran the AgentDojo banking suite against multiple attacks, covering injected attacks, normal content, and fraud-related non-attacks.
          </p>
        </Section>
        <Section title="Experiment 2 — Complied vs Resisted" figures={jlensCausationFigures}>
          <p>
            I sampled each prompt + injection k=8 times. Holding the system prompt, user prompt and injection fixed, some generations complied and some resisted, giving 112 pairs.
            Between the two, there was no divergence in how strongly the injection was detected (Figures 4 and 5).
          </p>
        </Section>
      </>
    ),
  },
  {
    slug: 'shimmer',
    title: 'Shimmer: Water Caustics Background Effect',
    short: 'Shimmer',
    date: 'August 2026',
    link: 'https://nathanbuilds.dev/shimmer/',
    img: shimmer,
    body: (
      <Section figures={[{ src: shimmer, alt: 'sunlight reflected off water shimmering on a wall through a window' }]}>
        <p>
          In a cabin near a lake in New Hampshire, if you sit in the right place a little before sunset, the light bounces up off the lake, through a window, and onto a wall.
          It makes a beautiful shimmering pattern, caused by the perturbations of the water and the geometry of the window pane&apos;s shifting shadow.
          This is my best approximation of the effect, using WebGL light simulations to try to capture the experience.
        </p>
      </Section>
    ),
  },
  {
    slug: 'j-bluff',
    title: 'J-Bluff: Reading a Language Model\'s Mind',
    short: 'J-Bluff',
    date: 'August 2026',
    link: 'https://nathanbuilds.dev/jlens/',
    img: jBluff,
    imgPosition: 'left',
    body: (
      <Section figures={[{ src: jBluff, alt: 'probing the model and pinning a token in the J-lens read-out' }]}>
        <p>
          Anthropic&apos;s
          {' '}
          <a href="https://transformer-circuits.pub/2026/workspace/index.html">global workspace paper</a>
          {' '}
          showed a training-free way to probe the internals of a model for arbitrary concepts.
        </p>
        <p>
          My game challenges the player to guess a secret that the model knows, but won&apos;t say.
          Using clever prompting and the J-lens tool, you can read verbalizable representations of what the model is &quot;thinking&quot;.
        </p>
      </Section>
    ),
  },
  {
    slug: 'walking-simulation',
    title: 'Downtown Seattle Walking Simulation',
    short: 'Walking Sim',
    date: 'August 2026',
    img: walking,
    body: (
      <Section figures={[{ src: walking, alt: 'a simulated walker crossing downtown Seattle while crosswalk signals cycle' }]}>
        <p>
          My daily commute requires me to walk from Westlake Light Rail Station to the Amazon Re:Invent office.
          While there is only one shortest path, the quickest path is a much harder problem.
          My coworker swears that their route is faster than mine. I built this to prove them wrong.
        </p>
        <p>
          For a walking commute, the timing of the stoplights and crosswalks determines which path is actually fastest.
          Taking a slightly longer route, but skipping a wait could make the total trip quicker.
          If you&apos;re willing to jog to catch a crosswalk, your opportunities expand.
        </p>
        <p>
          I took data from OpenStreetMap, King County indoor station maps, and city planning documents on light timing to make a realistic simulation.
          By defining strategies (station exit, building entrance, jogging) the simulation plots the path of a limited-information pedestrian.
          It reacts to stoplights within its FOV, switching paths on the fly.
        </p>
      </Section>
    ),
  },
  {
    slug: 'library-bingo',
    title: 'Library Bingo: Summer Reading Card Optimizer',
    short: 'Library Bingo',
    date: 'July 2026',
    link: 'https://nathanbuilds.dev/bingo/',
    img: bingo,
    body: (
      <Section figures={[{ src: bingo, alt: 'the bingo board filling in as books are added' }]}>
        <p>
          The King County Library System offers an adult summer reading bingo, with a grid of book categories (e.g., Time Travel, Horror) and a prize for bingo or blackout.
        </p>
        <p>
          I wanted to see if the books I had already read would qualify me for a bingo.
          Each book can qualify for multiple categories (e.g., a Horror book with Time Travel in it), but can only be used in one.
          My site used bipartite graph matching to optimize for the number of bingos possible with a set of books.
        </p>
        <p>
          I ended up using it to track my book list through the summer, and managed 4 bingos.
        </p>
      </Section>
    ),
  },
  {
    slug: 'set-game',
    title: 'Set Game Player',
    short: 'Set Game',
    date: 'November 2025',
    img: setGame,
    body: (
      <Section figures={[{ src: setGame, alt: 'detecting and classifying Set cards on a webcam frame and highlighting a valid set' }]}>
        <p>
          The card game Set requires you to quickly spot patterns of &quot;3 of a kind&quot; or &quot;all three different&quot; for the card attributes of color, number, shape, and fill.
          The goal of the game is to call out &quot;Set!&quot; when you see one, and collect the cards for scoring.
        </p>
        <p>
          I used OpenCV to extract card boundaries, determine attributes, and find sets. Unfortunately my system was banned from game night.
        </p>
      </Section>
    ),
  },
  {
    slug: 'knitting',
    title: 'Knitting Chart Tracker',
    short: 'Knitting Tracker',
    date: 'September 2025',
    img: knitting,
    body: (
      <Section figures={[{ src: knitting, alt: 'stepping through the cells of a pixel-art chart with a grid overlay' }]}>
        <p>
          My partner likes to knit, but complicated patterns require the knitter to remember their position within a large grid of stitches.
          I built a site that can snap a grid over any pattern, then track where the knitter has been.
        </p>
      </Section>
    ),
  },
  {
    slug: 'kobold-broker',
    title: 'Kobold Broker: Dark and Darker Market Prices',
    short: 'Kobold Broker',
    date: 'August 2024',
    img: market,
    body: (
      <Section figures={koboldFigures}>
        <p>
          The video game Dark and Darker has a player-to-player marketplace where players buy and sell items from their dungeon runs.
          Each item is unique, with random attributes, and despite over 20,000 players buying and selling, only a small fraction have the experience and patience for proper appraisal.
          By carefully evaluating attributes, you could find under-priced items and flip them for profit. Seeing the opportunity for arbitrage, I decided I needed data.
        </p>
        <p>
          The marketplace UI allowed you to request a page of items, but the UI had a forced cooldown before you could request another page.
          I suspected that even if there was a cooldown in the UI, it might not be as strongly enforced by the server.
          I started by trying to understand how data was being transmitted, and turned to Wireshark.
          Wireshark showed me unencrypted protobuf traffic, and decompiling the game files gave me the raw .proto files.
          This let me call the server directly, for which there was no request cooldown.
        </p>
        <p>
          Using the decompiled files and Wireshark, I reverse engineered their login flow and built my own headless client.
          This revealed another assumption: they didn&apos;t consider attackers on the same network to be a threat.
          I was able to use plaintext session tokens sent to the server to pose as my account, just by snooping on the traffic.
          On a compromised connection or public wifi, anyone on the network could take over an account and transfer away its valuable items.
        </p>
        <p>
          After a proof of concept of both auto-trading and account spoofing, I wrote up my findings and reported everything to the development team.
          They updated me later, saying that they changed their login flow, and encrypted the market traffic.
        </p>
      </Section>
    ),
  },
  {
    slug: 'valorant',
    title: 'Comfort Pick: A statistical study of Valorant matches',
    short: 'Valorant Comfort Pick',
    date: 'June 2023',
    img: comp,
    body: <Valorant />,
  },
  {
    slug: 'mani',
    title: 'MANI: Gesture Controlled Virtual Assistant',
    short: 'MANI',
    date: 'November 2022 - June 2022',
    img: MANI,
    body: (
      <Section figures={[{ src: MANI, alt: 'computer vision recognizing hand signs' }]}>
        <p>
          For a senior project, I led a team of peers to create a gesture-based virtual assistant.
          MANI utilized computer vision and machine learning to recognize finger spelled letters and autocomplete queries.
          Powered by Google assistant, MANI is able to display the weather, answer questions, control home automation, and more.
          The system was deployed on a Raspberry Pi, which was able to run powerful pose detection, hand tracking, and classification models with the help of Google Coral TPUs.
        </p>
      </Section>
    ),
  },
  {
    slug: 'rl-robot',
    title: 'Reinforcement Learning with a Robotic Manipulator',
    short: 'RL Robotic Arm',
    date: 'November 2021',
    img: RL,
    body: <RLRobot />,
  },
  {
    slug: 'engs21',
    title: 'Configurable accessible keyboard prototypes (ENGS21)',
    short: 'Accessible Keyboard',
    date: 'January 2022',
    img: wiring,
    body: <ENGS21 />,
  },
  {
    slug: 'squirrel-cam',
    title: 'Squirrel-Cam Bird Feeder Defense System',
    short: 'Squirrel-Cam',
    date: 'June 2020 - Present',
    img: Squirrel,
    body: (
      <Section figures={[{ src: Squirrel, alt: 'squirrel in crosshairs' }]}>
        <p>
          When it comes to bird feeders, squirrels are a tenacious threat.
          Using near-infrared spotlights and cameras, combined with a Tensorflow object recognition model,
          intelligent surveillance was established.
          Once identified, the program running on a Raspberry Pi will use its voltage pins to trigger irrigation equipment to spray the offending squirrels.
        </p>
      </Section>
    ),
  },
  {
    slug: 'vox-daily',
    title: 'Vox Daily Platform',
    short: 'Vox Daily',
    date: 'September 2020 - December 2020',
    img: Vox,
    body: (
      <Section figures={[{ src: Vox, alt: 'Vox daily logo' }]}>
        <p>
          Partnering with the DALI Lab, Dartmouth College sought to remake its faculty news aggregation platform.
          On this project, I implemented a React/Redux frontend, Node.js backend, and MongoDB stack from the ground up.
        </p>
      </Section>
    ),
  },
  {
    slug: 'moonboard',
    title: 'Moonboard Difficulty Classification',
    short: 'Moonboard',
    date: 'September 2019',
    img: Moonboard,
    body: (
      <Section figures={[{ src: Moonboard, alt: 'moonboard climbing route' }]}>
        <p>
          Climbing community members catalog routes for a standardized climbing wall call Moonboard.
          Using thousands of these routes, the difficulties of routes were estimated based on the holds available.
          Multiple machine learning algorithms, such as Random Forest and Neural networks, were compared.
        </p>
      </Section>
    ),
  },
  {
    slug: 'slar',
    title: 'Sign Language Augmented Reality',
    short: 'SLAR',
    date: 'May 2020 - September 2020',
    img: SLAR,
    body: (
      <Section figures={[{ src: SLAR, alt: 'Sign language in VR' }]}>
        <p>
          The Sign Language Augmented Reality (SLAR) project was designed and developed in the DALI Lab,
          using Unity and C#.
          I collected data for and designed two new evaluation metrics for determining the correctness of a hand sign.
        </p>
      </Section>
    ),
  },
];

export default projects;
